/**
 * @desc 解析应用信息
 * @time 2023-07-31 16:04:06
 * @user simxin
 */

import { requestConsole } from '@src/utils/api';
import { stripRoutePure, isSimpleAppMode } from '@src/utils/tools';

/**
 * @desc 应用信息
 */
export const parseApp = async (app: string): Promise<Application.Console> => {
  const mock = await requestConsole();
  return mock.find(e => e.path === app)!;
};

/**
 * @todo
 * @desc 应用资源
 * 先匹配菜单中的资源，匹配不到再匹配应用资源
 * 路由有两级时，严格按照两级匹配
 * 路由有一级时，匹配一级菜单，和有二级，且一级为 / 的菜单项
 */
export const parseSouce = async (
  app: string,
  chat: string,
  menu?: string,
  submenu?: string,
): Promise<{ js: string[]; css: string[] }> => {
  const mock = await requestConsole();
  const appInfo = mock.find(e => e.path === app);
  // 访问了错误的 app/menu 时, 会被重定向的
  if (!appInfo?.menus) {
    return {
      js: [],
      css: [],
    };
  }

  if (chat === 'chat') {
    // 始终使用新控制台逻辑
    const menuInfo = appInfo.menus.find(({ path }) => {
      return path === 'chat';
    });

    return {
      js: menuInfo!.js!,
      css: menuInfo!.css!,
    };
  } else {
    const menuInfo = appInfo.menus.find(m => m.path === menu);
    const subMenuInfo = appInfo.menus
      ?.flatMap?.(curMenu => curMenu.submenus)
      ?.find?.(s => s.path === menu);
    if (subMenuInfo?.js?.length) {
      return {
        js: subMenuInfo!.js,
        css: subMenuInfo!.css,
      };
    } else if (menuInfo?.js?.length) {
      return {
        js: menuInfo!.js,
        css: menuInfo!.css,
      };
    } else {
      return {
        js: appInfo!.js,
        css: appInfo!.css,
      };
    }
  }
};

/**
 * @desc 路由对应应用 path
 * @param url: /trtc/allapp/detail
 * @param ischat: 是否为 chat 逻辑
 * @return { app: "trtc", menu: "allapp", submenu: "detail" }
 * @patch chat 特殊逻辑，简易路由下依然有 app 层级
 */
export const parseRouterParam = async (
  url: string,
  ischat?: boolean,
): Promise<{ app: string; chat: string; menu: string; submenu: string }> => {
  if (!/^\//.test(url)) url = window.location.pathname;
  url = stripRoutePure(url);
  let app, chat, menu, submenu;
  if (isSimpleAppMode()) {
    [chat] = url.split('/');
  } else {
    [app, chat] = url.split('/');
  }
  if (isSimpleAppMode()) {
    if (chat === 'chat' && ischat) {
      [chat, menu, submenu] = url.split('/');
      app = await parseAppStrWithMenu(chat);
    } else {
      [menu, submenu] = url.split('/');
      app = await parseAppStrWithMenu(menu);
    }
  } else {
    if (chat === 'chat' && ischat) {
      [app, chat, menu, submenu] = url.split('/');
    } else {
      [app, menu, submenu] = url.split('/');
    }
  }
  return { app, chat, menu, submenu };
};

/**
 * @desc 通过菜单向上找应用
 */
export const parseAppStrWithMenu = async (menu: string): Promise<string> => {
  menu = menu || '/';
  const mock = await requestConsole();
  const app = mock.find(a =>
    a.menus.find(m => {
      if (m.path === '/' && m.submenus?.length) {
        return m.submenus.find(s => s.path === menu);
      }
      return m.path === menu;
    }),
  );
  // 找不到路由，重定向到 calls 页
  if (!app) {
    console.log('重定向');
    window.location.href = '/calls';
    return '';
  }
  return app.path;
};
